<template>
  <a-card title="退款工具">
    <a-form layout="inline" :model="filters" class="search-form" @finish="search">
      <a-form-item label="用户 ID">
        <a-input v-model:value="filters.user_id" allow-clear placeholder="请输入用户 ID" />
      </a-form-item>
      <a-form-item label="退款状态">
        <a-select v-model:value="filters.status" allow-clear placeholder="全部" :options="statusOptions" class="status-select" />
      </a-form-item>
      <a-form-item label="登记时间">
        <a-range-picker
          v-model:value="filters.createdRange"
          show-time
          value-format="YYYY-MM-DD HH:mm:ss"
          format="YYYY-MM-DD HH:mm"
        />
      </a-form-item>
      <a-form-item>
        <a-space>
          <a-button type="primary" html-type="submit">查询</a-button>
          <a-button type="primary" ghost @click="openCreate">登记退款</a-button>
        </a-space>
      </a-form-item>
    </a-form>

    <a-table
      v-if="hasSearched"
      row-key="id"
      :columns="columns"
      :data-source="records"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
    >
      <template #headerCell="{ column }">
        <template v-if="column.key === 'refund_amount'">
          <span>
            退款金额
            <a-tooltip title="退款金额（分）= 向下取整（套餐金额 ×（1 - 平台手续费率）- 消耗积分 × 3.8）；iOS 手续费率为 30%，Android 手续费率为 0.6%。">
              <QuestionCircleOutlined class="formula-tip" />
            </a-tooltip>
          </span>
        </template>
        <template v-else-if="column.key === 'actions'">
          <span>
            操作
            <a-tooltip title="仅更改退款登记状态，不修改订单状态。">
              <QuestionCircleOutlined class="formula-tip" />
            </a-tooltip>
          </span>
        </template>
        <template v-else>{{ column.title }}</template>
      </template>
      <template #package_amount="{ record }">{{ centsToYuan(record.package_amount) }}</template>
      <template #refund_amount="{ record }">{{ centsToYuan(record.refund_amount) }}</template>
      <template #refund_reason="{ record }"><span class="refund-reason">{{ record.refund_reason }}</span></template>
      <template #transaction_id="{ record }"><span class="transaction-id">{{ record.transaction_id }}</span></template>
      <template #registrant_id="{ record }">
        {{ record.registrant_id }}
        <div v-if="record.registrant_user_name">({{ record.registrant_user_name }})</div>
      </template>
      <template #operator_id="{ record }">
        {{ record.operator_id || '' }}
        <div v-if="record.operator_user_name">({{ record.operator_user_name }})</div>
      </template>
      <template #status="{ record }">
        <a-tag :color="statusColor(record.status)">{{ statusLabel(record.status) }}</a-tag>
      </template>
      <template #created_at="{ record }">
        <span class="time-value"><span>{{ datePart(record.created_at) }}</span><span>{{ timePart(record.created_at) }}</span></span>
      </template>
      <template #order_time="{ record }">
        <span class="time-value"><span>{{ datePart(record.order_time) }}</span><span>{{ timePart(record.order_time) }}</span></span>
      </template>
      <template #actions="{ record }">
        <a-space v-if="record.status === 'REGISTERED' && canOperate" direction="vertical" :size="8">
          <a-button type="primary" size="small" @click="openStatusModal(record, 'REFUNDED')">标记已退款</a-button>
          <a-button danger size="small" @click="openStatusModal(record, 'REJECTED')">不可退</a-button>
        </a-space>
        <a-button v-else-if="record.status === 'REGISTERED'" type="link" danger size="small" @click="notifyNoPermission">无操作权限</a-button>
        <span v-else>-</span>
      </template>
    </a-table>
  </a-card>

  <a-modal v-model:open="statusModalOpen" :title="statusModalTitle" :confirm-loading="updating" @ok="submitStatusUpdate" @cancel="resetStatusForm">
    <a-form :label-col="{ span: 6 }">
      <a-form-item :label="statusForm.status === 'REFUNDED' ? '允许退款原因' : '不可退款原因'" required>
        <a-textarea v-model:value="statusForm.remark" :rows="3" placeholder="请输入操作原因" />
      </a-form-item>
    </a-form>
  </a-modal>

  <a-modal v-model:open="createOpen" title="登记退款" :confirm-loading="creating" @ok="createRefund" @cancel="resetCreateForm">
    <a-form :model="createForm" :label-col="{ span: 6 }">
      <a-form-item label="用户 ID" required>
        <a-input-search v-model:value="createForm.user_id" placeholder="输入后查询最近订单" enter-button="查询订单" :loading="orderLoading" @search="loadOrder" />
      </a-form-item>
      <template v-if="order">
        <a-descriptions size="small" :column="2" bordered class="order-summary">
          <a-descriptions-item label="套餐">{{ order.vip_meal_name }}</a-descriptions-item>
          <a-descriptions-item label="套餐金额">{{ centsToYuan(order.package_amount) }}</a-descriptions-item>
          <a-descriptions-item label="平台">{{ order.platform }}</a-descriptions-item>
          <a-descriptions-item label="订单时间">{{ order.order_time }}</a-descriptions-item>
          <a-descriptions-item label="交易 ID" :span="2">{{ order.transaction_id }}</a-descriptions-item>
        </a-descriptions>
        <a-form-item label="平台" required class="form-item-top">
          <a-select
            v-model:value="createForm.platform"
            :options="platformOptions"
            :disabled="Boolean(order.platform)"
            placeholder="请选择平台"
            class="full-width"
          />
        </a-form-item>
        <a-form-item label="消耗会员积分" required class="form-item-top">
          <a-input-number v-model:value="createForm.consumed_points" :min="0" :precision="0" class="full-width" />
        </a-form-item>
        <a-form-item label="退款原因" required>
          <a-textarea v-model:value="createForm.refund_reason" :rows="3" placeholder="请输入退款原因" />
        </a-form-item>
        <a-form-item label="支付宝账号" required>
          <a-input v-model:value="createForm.alipay_account" placeholder="可退款时必填" />
        </a-form-item>
      </template>
      <a-empty v-else description="请先通过用户 ID 查询订单" />
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { message } from 'ant-design-vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'
import { useStore } from 'vuex'
import request from '@/utils/request'

type RefundRecord = {
  id: number
  user_id: number
  package_type: string
  package_amount: number
  consumed_points: number
  refund_reason: string
  alipay_account: string
  refund_amount: number
  transaction_id: string
  platform: string
  registrant_id: number
  registrant_user_name?: string | null
  operator_id: number
  operator_user_name?: string | null
  status: string
  order_time: string
  created_at: string
}

type RefundOrder = {
  order_id: string
  vip_meal_name: string
  package_amount: number
  transaction_id: string
  platform: string
  order_time: string
}

const loading = ref(false)
const creating = ref(false)
const orderLoading = ref(false)
const createOpen = ref(false)
const statusModalOpen = ref(false)
const updating = ref(false)
const records = ref<RefundRecord[]>([])
const hasSearched = ref(false)
const order = ref<RefundOrder | null>(null)
const store = useStore()
const canOperate = computed(() => Number(store.state.user?.user_role) === 4 || [862515, 548814, 37].includes(Number(store.state.user?.user_id)))
const filters = reactive({ user_id: '', status: 'REGISTERED' as string | undefined, createdRange: null as string[] | null })
const createForm = reactive({ user_id: '', platform: '', consumed_points: 0, refund_reason: '', alipay_account: '' })
const statusForm = reactive({ record: null as RefundRecord | null, status: '' as 'REFUNDED' | 'REJECTED' | '', remark: '' })
const pagination = reactive({ total: 0, current: 1, pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] })

const statusOptions = [
  { label: '已登记', value: 'REGISTERED' },
  { label: '已退款', value: 'REFUNDED' },
  { label: '不可退', value: 'REJECTED' },
]

const platformOptions = [
  { label: 'iOS', value: 'IOS' },
  { label: 'Android', value: 'ANDROID' },
]

const columns = [
  { title: '用户 ID', dataIndex: 'user_id', width: 85, customCell: () => ({ class: 'single-line-cell' }) },
  { title: '套餐', dataIndex: 'package_type', width: 80 },
  { title: '套餐金额', key: 'package_amount', slots: { customRender: 'package_amount' }, width: 90 },
  { title: '消耗积分', dataIndex: 'consumed_points', width: 105, customCell: () => ({ class: 'single-line-cell' }) },
  { title: '退款金额', key: 'refund_amount', slots: { customRender: 'refund_amount' }, width: 90 },
  { title: '退款原因', key: 'refund_reason', slots: { customRender: 'refund_reason' }, width: 140 },
  { title: '支付宝账号', dataIndex: 'alipay_account', ellipsis: true, width: 130 },
  { title: '交易 ID', key: 'transaction_id', slots: { customRender: 'transaction_id' }, width: 160 },
  { title: '平台', dataIndex: 'platform', width: 90 },
  { title: '登记人', key: 'registrant_id', slots: { customRender: 'registrant_id' }, width: 100 },
  { title: '操作人', key: 'operator_id', slots: { customRender: 'operator_id' }, width: 100 },
  { title: '状态', key: 'status', slots: { customRender: 'status' }, width: 100 },
  { title: '登记时间', key: 'created_at', slots: { customRender: 'created_at' }, width: 110 },
  { title: '订单时间', key: 'order_time', slots: { customRender: 'order_time' }, width: 110 },
  { title: '操作', key: 'actions', slots: { customRender: 'actions' }, fixed: 'right', width: 150 },
]

const centsToYuan = (cents: number) => `¥${((cents || 0) / 100).toFixed(2)}`
const formatDate = (value: string) => value || '-'
const datePart = (value: string) => formatDate(value).replace('T', ' ').split(' ')[0]
const timePart = (value: string) => formatDate(value).replace('T', ' ').split(' ')[1] || ''
const statusLabel = (status: string) => ({ REGISTERED: '已登记', REFUNDED: '已退款', REJECTED: '不可退' }[status] || status)
const statusColor = (status: string) => ({ REGISTERED: 'processing', REFUNDED: 'success', REJECTED: 'default' }[status] || 'default')
const statusModalTitle = computed(() => statusForm.status === 'REFUNDED' ? '标记已退款' : '标记不可退')

async function loadRecords(page = pagination.current, pageSize = pagination.pageSize) {
  loading.value = true
  try {
    const response: any = await request.get('/api/user/refunds/search', {
      user_id: filters.user_id,
      status: filters.status,
      created_start: filters.createdRange?.[0],
      created_end: filters.createdRange?.[1],
      offset: (page - 1) * pageSize,
      limit: pageSize,
    })
    const data = response?.data || {}
    records.value = data.list || []
    pagination.total = data.pagination?.total || 0
    pagination.current = page
    pagination.pageSize = pageSize
  } catch (_error) {
    records.value = []
    pagination.total = 0
    pagination.current = page
    pagination.pageSize = pageSize
  } finally {
    loading.value = false
  }
}

function search() {
  hasSearched.value = true
  loadRecords(1, pagination.pageSize)
}
onMounted(() => search())
function handleTableChange(page: { current: number; pageSize: number }) { loadRecords(page.current, page.pageSize) }
function openCreate() { createOpen.value = true }
function resetCreateForm() {
  order.value = null
  createForm.user_id = ''
  createForm.platform = ''
  createForm.consumed_points = 0
  createForm.refund_reason = ''
  createForm.alipay_account = ''
}

async function loadOrder() {
  if (!createForm.user_id) return message.warning('请输入用户 ID')
  orderLoading.value = true
  order.value = null
  try {
    const orderResponse: any = await request.get('/api/user/refunds/orders', { user_id: createForm.user_id })
    order.value = orderResponse.data
    createForm.platform = String(order.value?.platform || '').toUpperCase()
    if (!order.value?.order_time) {
      order.value = null
      orderLoading.value = false
      return message.error('未查询到可登记退款的订单')
    }
  } catch (_error) {
    order.value = null
    createForm.platform = ''
    createForm.consumed_points = 0
    orderLoading.value = false
    message.error('订单查询失败，请重试')
    return
  }

  try {
    const pointsResponse: any = await request.get('/api/user/refunds/points', { user_id: createForm.user_id, order_time: order.value.order_time })
    createForm.consumed_points = pointsResponse.data?.member_points || 0
  } catch (_error) {
    createForm.consumed_points = 0
    message.warning('查询消耗积分失败，请手工填写')
  } finally {
    orderLoading.value = false
  }
}

async function createRefund() {
  if (!order.value || !createForm.refund_reason.trim()) return message.warning('请先查询订单并填写退款原因')
  if (!createForm.platform) return message.warning('请选择平台')
  if (!createForm.alipay_account.trim()) return message.warning('请输入支付宝账号')
  creating.value = true
  try {
    await request.post('/api/user/refunds/create', {
      user_id: Number(createForm.user_id),
      order_id: order.value.order_id,
      consumed_points: createForm.consumed_points,
      refund_reason: createForm.refund_reason,
      alipay_account: createForm.alipay_account,
      platform: createForm.platform,
    })
    createOpen.value = false
    resetCreateForm()
    hasSearched.value = true
    loadRecords(1, pagination.pageSize)
  } catch (_error) {
    message.error('退款登记失败，请重试')
  } finally {
    creating.value = false
  }
}

function openStatusModal(record: RefundRecord, status: 'REFUNDED' | 'REJECTED') {
  if (!canOperate.value) return notifyNoPermission()
  statusForm.record = record
  statusForm.status = status
  statusForm.remark = ''
  statusModalOpen.value = true
}

function resetStatusForm() {
  statusForm.record = null
  statusForm.status = ''
  statusForm.remark = ''
}

async function submitStatusUpdate() {
  if (!statusForm.record || !statusForm.status || !statusForm.remark.trim()) return message.warning('请填写操作原因')
  updating.value = true
  try {
    await request.put(`/api/user/refunds/${statusForm.record.id}/update`, { status: statusForm.status, remark: statusForm.remark })
    statusModalOpen.value = false
    resetStatusForm()
    loadRecords()
  } catch (_error) {
    message.error('更新退款状态失败，请重试')
  } finally {
    updating.value = false
  }
}

function notifyNoPermission() { message.warning('无操作权限') }

</script>

<style scoped>
.search-form { margin-bottom: 16px; }
.status-select { width: 120px; }
.order-summary { margin-bottom: 20px; }
.form-item-top { margin-top: 20px; }
.full-width { width: 100%; }
.transaction-id { word-break: break-all; }
.refund-reason { white-space: pre-line; word-break: break-word; }
.time-value { display: inline-flex; flex-direction: column; white-space: nowrap; }
:deep(.single-line-cell) { white-space: nowrap; }
.formula-tip { margin-left: 4px; color: #8c8c8c; cursor: help; }
</style>
